import React, { Component, useEffect } from 'react';
import { Form, FormGroup, Input, Label } from 'reactstrap';
import PropTypes from 'prop-types';

import Body from '../Components/layout/Body';
import OtherTextInput from '../Components/layout/OtherTextInput';
import { ACTIVITY_BUTTON_GROUPS } from './ConstActivityBtns';
import { GOALS } from './ConstGoalValues';
import { OTHER_CKBX_VAL, isOtherChecked } from './OtherCheckboxUtils';

const ACTIVITIES_HELP = `
  <div>
    <p><strong>Purpose:</strong> Everything flows from the client’s goals and activities: what do they want to do that they currently have difficulty doing?  
    If the client’s goals are still emerging or not yet well-defined, see if you can help the client choose some sort of starting point, something that they are interested in doing.</p>
    <p><strong>Resources:</strong> Here are a few videos that might spark some interest:</p>
    <ul>
      <li><a href="https://youtu.be/DoZ0RmJZzcs" target="_blank">Consumer Highlight: Austin McNew</a>. Austin has a C4 spinal cord injury and controls his iPhone and wheelchair using head switches. <span class="aa-minor-note">From the <a href="https://www.eastersealstech.com/about/the-indiana-assistive-technology-indata-project/" target="_blank">INDATA project</a>.</span></li>
      <li><a href="https://youtu.be/4s9vCFkmabs" target="_blank">Smart Homes are Game Changer for People with Disabilities</a>. Todd has a cervical spinal cord injury and controls his iPhone using speech and joystick switches. <span class="aa-minor-note">From NBC News.</span></li>
      <li><a href="https://youtu.be/7oT6SIu2zuw" target="_blank">Introduction to Brad's eye tracking device</a>. Brad has cerebral palsy and uses eye tracking to control his iPad for communication and other apps. <span class="aa-minor-note">From <a href="https://www.youtube.com/@AllAccessLife" target="_blank">All Access Life</a>.</span></li>
      <li><a href="https://youtu.be/uhGwu_HuImM" target="_blank">Quadriplegic Drone Pilot</a>. Chris has cerebral palsy and uses switches and head tracking. Here he shows how he pilots a drone with his setup. <span class="aa-minor-note">From <a href="https://www.youtube.com/@icdhills" target="_blank">Christopher Hills</a>.</span></li>
      <li><a href="https://youtu.be/9fcK19CAjWM" target="_blank">Xbox Adaptive Controller</a>. A variety of folks use alternative access to play computer games.</li>
    </ul>
    <p>We're not endorsing any specific products or approaches shown in these videos, but just trying to give you an idea of some possibilities.</p>
  </div>
`;

const propTypes = {
  firstName: PropTypes.string,
  mainGoal: PropTypes.string,
  activities: PropTypes.arrayOf(PropTypes.string),
  onChangeActivities: PropTypes.func.isRequired,
  saveFormData: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired
};

const defaultProps = {
  firstName: 'the client',
  mainGoal: GOALS.FULL,
  activities: []
};

//content sub-component with unique content for this page
class GoalsActivitiesContent extends Component {

  //Render the form for the activities.
  //Calls the setter that's passed in by the parent, when a radio button is clicked.
  //That setter changes the state of the activities in the parent, 
  //which triggers a new render of the view with the new goal.

  render(){
    const mainGoal = this.props.mainGoal;
    const activities = this.props.activities ?? []; //initialize to empty array if null or undefined happens to be passed in
    const onChangeActivities= this.props.onChangeActivities;
    const otherActivity = this.props.otherActivity;
    const onChangeOtherActivity = this.props.onChangeOtherActivity;
    console.log("rendering GatherInfoGoalsActivitiesForm with activities: " + activities);

    let btnGroups = ACTIVITY_BUTTON_GROUPS; 
    let otherChecked = isOtherChecked(activities);
    return(
    <>
    <Form>
      <FormGroup tag="fieldset">
        <legend>Check all that apply:</legend>
        {btnGroups.map((btnGroup, grpIndex) => {
          return(
            <FormGroup tag="fieldset" key={grpIndex}>
              <legend>{btnGroup.legend}</legend>
              {btnGroup.buttons.map((btn, index) =>
                <React.Fragment key={index}>
                <FormGroup check>
                  <Input
                    id={btn.value}
                    value={btn.value}
                    checked={activities.findIndex( (activity) => activity.startsWith(btn.value)) > -1}
                    // checked={activities.indexOf(btn.value) > -1}
                    onChange={onChangeActivities} 
                    name="activity"
                    type="checkbox"
                  />
                  {' '}
                  <Label for={btn.value} check>
                    {btn.label}
                    {' '}
                    {/* <span className="aa-more-info">{btn.moreInfo}</span> */}
                    <span>{btn.moreInfo}</span>
                  </Label>
                </FormGroup>
                {/* If the btn is 'other', include a text field to specify */}
                { ((btn.value === OTHER_CKBX_VAL)) 
                  ?
                  <OtherTextInput
                    otherChecked={otherChecked}
                    otherText={otherActivity}
                    onChangeOtherText={onChangeOtherActivity}
                  />
                  : null
                }
                </React.Fragment>  
              )}
            </FormGroup>
          )
        })}
      </FormGroup>
    </Form>
    </>
  );
}
}

//main component for this page
//basic pattern is to set up the pageData, define the unique content for the page (above),
//then render the page using <Body> component.
//since the main goal step includes a form, receive the initial form values via props,
//along with setters to change those values based on the form input.
function GatherInfoGoalsActivities(props) {
  const firstName = props.firstName;
  const mainGoal = props.mainGoal;
  const activities = props.activities;
  const onChangeActivities = props.onChangeActivities;
  const otherActivity = props.otherActivity;
  const onChangeOtherActivity = props.onChangeOtherActivity;
  const saveFormData = props.saveFormData;
  const onContinue = props.onContinue;

  console.log("rendering GatherInfoGoalsActivities, activities: " + activities);

  const pageData = {
    image: "", //not yet used
    faIcon: "",
    pageSymbolClassName: "aa-section-symbol gather",
    title: `What activities does ${firstName} want or need to do?`,
    subTitle: `Try to identify and focus on activities that are engaging and important to ${firstName}.`,
    learnMore: {linkText: "Imagine Possibilities and learn more about what activities are possible",
                title: "Imagine Possibilities",
                content: ACTIVITIES_HELP
                },
    nextRoute: "", //not used since we have an onContinue callback
    sidebar: true
  }

  useEffect(() => {
    console.log('GIGActivities mounted');
    return () => {
      console.log('GIG unmounting and saving...');
      saveFormData();
    }
  }, [saveFormData])

  return(
    <>
    <Body page={pageData} 
      contentComponent={
        <GoalsActivitiesContent 
          mainGoal={mainGoal} 
          activities={activities} 
          onChangeActivities={onChangeActivities}
          otherActivity={otherActivity}
          onChangeOtherActivity={onChangeOtherActivity}
        />}
      onContinue={onContinue} 
    />
    </>
  );
}

GatherInfoGoalsActivities.propTypes = propTypes;
GatherInfoGoalsActivities.defaultProps = defaultProps;

export default GatherInfoGoalsActivities;
